import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Button, Chip, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, MenuItem,
  Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Paper, IconButton, Tooltip, Alert
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import Layout from '../components/Layout';
import axios from 'axios';
import api, { projectService } from '../services/api';

const STATUS_COLORS = {
  draft:     'default',
  submitted: 'info',
  approved:  'success',
  rejected:  'error',
};

const STATUSES = ['draft', 'submitted', 'approved', 'rejected'];

const defaultForm = {
  project_id: '',
  issued_by_type: 'gc_admin',
  vendor_id: '',
  invoice_id: '',
  change_order_id: '',
  contract_id: '',
  amount: '',
  reason: '',
  issued_date: new Date().toISOString().split('T')[0],
  memo_number: '',
};

export default function CreditMemos() {
  const [memos, setMemos]             = useState([]);
  const [projects, setProjects]       = useState([]);
  const [vendors, setVendors]         = useState([]);
  const [openCreate, setOpenCreate]   = useState(false);
  const [openStatus, setOpenStatus]   = useState(false);
  const [selectedMemo, setSelectedMemo] = useState(null);
  const [newStatus, setNewStatus]     = useState('');
  const [form, setForm]               = useState(defaultForm);
  const [error, setError]             = useState('');
  const [loading, setLoading]         = useState(false);

  const downloadPdf = async (memo) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        (process.env.REACT_APP_API_URL || 'https://construction-backend-531984037368.us-central1.run.app/api') + '/credit-memos/' + memo.id + '/pdf',
        { headers: { 'Authorization': 'Bearer ' + token } }
      );
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'credit-memo-' + (memo.memo_number || memo.id) + '.pdf';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) { console.error('PDF download failed', e); }
  };

  const fetchMemos = async () => {
    try {
      const res = await api.get('/credit-memos');
      setMemos(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error('Failed to fetch credit memos', err);
    }
  };

  const fetchProjects = async () => {
    try {
      const res = await projectService.getAll();
      console.log('projects response:', res.data);
      setProjects(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error('Failed to fetch projects', err);
    }
  };

  const fetchVendors = async () => {
    try {
      const res = await api.get('/vendors');
      setVendors(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error('Failed to fetch vendors', err);
    }
  };

  useEffect(() => {
    fetchMemos();
    fetchProjects();
    fetchVendors();
  }, []);

  const handleCreate = async () => {
    const pid = form.project_id || (projects.length > 0 ? projects[0].id : null);
    if (!pid) { setError("No project available"); return; }
    
    if (!form.amount || parseFloat(form.amount) <= 0) { setError('Amount must be greater than 0'); return; }
    setLoading(true);
    setError('');
    try {
      await api.post(`/projects/${pid}/credit-memos`, {
        issued_by_type:  form.issued_by_type,
        vendor_id:       form.vendor_id       || undefined,
        invoice_id:      form.invoice_id      || undefined,
        change_order_id: form.change_order_id || undefined,
        contract_id:     form.contract_id     || undefined,
        amount:          form.amount,
        reason:          form.reason,
        issued_date:     form.issued_date,
        memo_number:     form.memo_number     || undefined,
      });
      setOpenCreate(false);
      setForm(defaultForm);
      fetchMemos();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create credit memo');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async () => {
    try {
      await api.patch(
        `/projects/${selectedMemo.project_id}/credit-memos/${selectedMemo.id}/status`,
        { status: newStatus }
      );
      setOpenStatus(false);
      fetchMemos();
    } catch (err) {
      console.error('Failed to update status', err);
    }
  };

  const openStatusDialog = (memo) => {
    setSelectedMemo(memo);
    setNewStatus(memo.status);
    setOpenStatus(true);
  };

  const totalAmount = memos.reduce((sum, m) => sum + parseFloat(m.amount || 0), 0);

  return (
    <Layout>
    
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ReceiptLongIcon color="primary" />
          <Typography variant="h5" fontWeight={600}>Credit Memos</Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => { setForm({ ...defaultForm, project_id: projects[0]?.id || '' }); setOpenCreate(true); }}>
          New Credit Memo
        </Button>
      </Box>

      <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
        {[
          { label: 'Total Credits', value: `$${totalAmount.toLocaleString('en-US', { minimumFractionDigits: 2 })}`, color: 'primary.main' },
          { label: 'Draft',     value: memos.filter(m => m.status === 'draft').length },
          { label: 'Submitted', value: memos.filter(m => m.status === 'submitted').length },
          { label: 'Approved',  value: memos.filter(m => m.status === 'approved').length },
        ].map((card) => (
          <Paper key={card.label} elevation={2} sx={{ p: 2, flex: 1, textAlign: 'center' }}>
            <Typography variant="h6" fontWeight={700} color={card.color || 'text.primary'}>{card.value}</Typography>
            <Typography variant="caption" color="text.secondary">{card.label}</Typography>
          </Paper>
        ))}
      </Box>

      <TableContainer component={Paper} elevation={2}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: 'grey.100' }}>
              <TableCell><strong>Memo #</strong></TableCell>
              <TableCell><strong>Issued By</strong></TableCell>
              <TableCell><strong>Amount</strong></TableCell>
              <TableCell><strong>Date</strong></TableCell>
              <TableCell><strong>Status</strong></TableCell>
              <TableCell><strong>Reason</strong></TableCell>
              <TableCell align="center"><strong>Actions</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {memos.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ color: 'text.secondary', py: 6 }}>
                  No credit memos yet. Click "New Credit Memo" to create one.
                </TableCell>
              </TableRow>
            ) : memos.map((memo) => (
              <TableRow key={memo.id} hover>
                <TableCell>{memo.memo_number}</TableCell>
                <TableCell>{memo.issued_by_type === 'vendor' ? memo.vendor_name || 'Vendor' : 'GC / Admin'}</TableCell>
                <TableCell><strong>${parseFloat(memo.amount).toLocaleString('en-US', { minimumFractionDigits: 2 })}</strong></TableCell>
                <TableCell>{memo.issued_date?.split('T')[0]}</TableCell>
                <TableCell>
                  <Chip
                    label={memo.status.charAt(0).toUpperCase() + memo.status.slice(1)}
                    color={STATUS_COLORS[memo.status]}
                    size="small"
                  />
                </TableCell>
                <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {memo.reason || '—'}
                </TableCell>
                <TableCell align="center">
                  <Tooltip title="Update Status">
                    <IconButton size="small" onClick={() => openStatusDialog(memo)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={openCreate} onClose={() => { setOpenCreate(false); setForm(defaultForm); setError(''); }} maxWidth="sm" fullWidth>
        <DialogTitle>New Credit Memo</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          {error && <Alert severity="error">{error}</Alert>}
          <TextField select label="Project" value={form.project_id} onChange={(e) => setForm({ ...form, project_id: e.target.value })} fullWidth required>
            <MenuItem value="">— Select Project —</MenuItem>
            {projects.map((p) => <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>)}
          </TextField>

          <TextField label="Memo Number (auto-generated if blank)" value={form.memo_number} onChange={(e) => setForm({ ...form, memo_number: e.target.value })} fullWidth />
          <TextField select label="Issued By" value={form.issued_by_type} onChange={(e) => setForm({ ...form, issued_by_type: e.target.value, vendor_id: '' })} fullWidth required>
            <MenuItem value="gc_admin">GC / Admin</MenuItem>
            <MenuItem value="vendor">Vendor</MenuItem>
          </TextField>
          {form.issued_by_type === 'vendor' && (
            <TextField select label="Vendor" value={form.vendor_id} onChange={(e) => setForm({ ...form, vendor_id: e.target.value })} fullWidth>
              <MenuItem value="">— None —</MenuItem>
              {vendors.map((v) => <MenuItem key={v.id} value={v.id}>{v.company_name || v.name}</MenuItem>)}
            </TextField>
          )}
          <TextField label="Amount ($)" type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} fullWidth required inputProps={{ min: 0, step: '0.01' }} />
          <TextField label="Issued Date" type="date" value={form.issued_date} onChange={(e) => setForm({ ...form, issued_date: e.target.value })} fullWidth InputLabelProps={{ shrink: true }} />
          <TextField label="Reason" multiline rows={3} value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} fullWidth />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setOpenCreate(false); setForm(defaultForm); setError(''); }}>Cancel</Button>
          <Button variant="contained" onClick={handleCreate} disabled={loading || !form.amount}>
            {loading ? 'Creating...' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openStatus} onClose={() => setOpenStatus(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Update Status — {selectedMemo?.memo_number}</DialogTitle>
        <DialogContent>
          <TextField select label="Status" value={newStatus} onChange={(e) => setNewStatus(e.target.value)} fullWidth sx={{ mt: 1 }}>
            {STATUSES.map((s) => <MenuItem key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</MenuItem>)}
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenStatus(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleStatusUpdate}>Update</Button>
        </DialogActions>
      </Dialog>
    </Box>
    </Layout>
  );
}
